import * as v from 'valibot'
import type {
  Calendar,
  CalendarStatus,
  CalendarSettingInput,
  CalendarDailyAvailability,
  CalendarDailyAvailabilityInput,
} from '@/types/schema.generated.ts'

export type FormType = {
  title: string
  description: string
  status: CalendarStatus
  durationMinutes: number
  offsetMinutes: number
  assignedStaffGUIDs: string[]
  dailyAvailabilities: CalendarDailyAvailabilityInput[]
}

const validFormSchema = v.object({
  title: v.pipe(
    v.string(),
    v.trim(),
    v.nonEmpty('必須です'),
    v.maxLength(256, '256文字以下にしてください')
  ),
  durationMinutes: v.pipe(
    v.number('数値を入力してください'),
    v.minValue(10, '10分以上にしてください'),
    v.maxValue(720, '720分以下にしてください')
  ),
  offsetMinutes: v.pipe(
    v.number('数値を入力してください'),
    v.minValue(10, '10分以上にしてください'),
    v.maxValue(720, '720分以下にしてください')
  ),
  assignedStaffGUIDs: v.pipe(
    v.array(v.string()),
    v.minLength(1, '最低1人は必須です')
  ),
})

export const validateForm = (form: FormType) => {
  const result = v.safeParse(validFormSchema, form, {
    abortPipeEarly: true,
  })

  if (result.success) {
    return undefined
  }

  const errors = v.flatten(result.issues).nested
  return {
    title: errors?.title?.[0],
    durationMinutes: errors?.durationMinutes?.[0],
    offsetMinutes: errors?.offsetMinutes?.[0],
    assignedStaffGUIDs: errors?.assignedStaffGUIDs?.[0],
  }
}

export const mapCalendarToForm = (calendar: Calendar): FormType => {
  return {
    status: calendar.status,
    title: calendar.title,
    description: calendar.description ? calendar.description : '',
    assignedStaffGUIDs: calendar.assignedStaffGUIDs,
    durationMinutes: calendar.slotDurationMinutes,
    offsetMinutes: calendar.slotOffsetMinutes,
    dailyAvailabilities: calendar.dailyAvailabilities.map(
      (da: CalendarDailyAvailability) => {
        return {
          dayOfWeek: da.dayOfWeek,
          from: {
            hours: da.from.hours,
            minutes: da.from.minutes,
          },
          until: {
            hours: da.until.hours,
            minutes: da.until.minutes,
          },
        }
      }
    ),
  }
}

export const mapFormToCalendarSettingInput = (
  form: FormType
): CalendarSettingInput => {
  const res: CalendarSettingInput = {
    title: form.title,
    status: form.status,
    description: form.description.length ? form.description : undefined,
    assignedStaffGUIDs: form.assignedStaffGUIDs,
    slotDurationMinutes: form.durationMinutes,
    slotOffsetMinutes: form.offsetMinutes,
    dailyAvailabilities: form.dailyAvailabilities,
  }
  return res
}
